import React from 'react';
import KnowledgeMedia from './KnowledgeMedia';

const INLINE_TOKEN = /(?:!?\[[^\]]*\]\([^\s)]+(?:\s+"[^"]*")?\)|`[^`\n]+`|\*\*[^*\n]+\*\*|__[^_\n]+__|~~[^~\n]+~~|\*[^*\n]+\*|_[^_\n]+_)/g;
const ATTACHMENT_BLOCK = /^\s*\{\{knowledge-file:([A-Za-z0-9_-]{1,64})\}\}\s*$/;

export function knowledgeAttachmentMarker(attachmentId) {
  return `{{knowledge-file:${attachmentId}}}`;
}

export function extractKnowledgeAttachmentIds(content) {
  const ids = [];
  const seen = new Set();
  let inCodeFence = false;
  String(content || '').replace(/\r\n?/g, '\n').split('\n').forEach(line => {
    if (/^\s*```/.test(line)) {
      inCodeFence = !inCodeFence;
      return;
    }
    if (inCodeFence) return;
    const match = line.match(ATTACHMENT_BLOCK);
    const id = match?.[1];
    if (!id) return;
    if (!seen.has(id)) {
      seen.add(id);
      ids.push(id);
    }
  });
  return ids;
}

export function removeKnowledgeAttachmentMarker(content, attachmentId) {
  let inCodeFence = false;
  const lines = String(content || '').replace(/\r\n?/g, '\n').split('\n').filter(line => {
    if (/^\s*```/.test(line)) {
      inCodeFence = !inCodeFence;
      return true;
    }
    const match = inCodeFence ? null : line.match(ATTACHMENT_BLOCK);
    return !match || String(match[1]) !== String(attachmentId);
  });
  return lines.join('\n').replace(/\n{3,}/g, '\n\n').trim();
}

function safeUrl(rawUrl, { image = false } = {}) {
  const value = String(rawUrl || '').trim();
  if (!value) return null;

  // Protocol-relative URLs are external too, but do not carry an explicit
  // scheme that we can validate below.
  if (value.startsWith('//')) return null;

  if (value.startsWith('/') || value.startsWith('./') || value.startsWith('../') || value.startsWith('#')) {
    return value;
  }

  try {
    const parsed = new URL(value);
    const protocols = image ? ['http:', 'https:'] : ['http:', 'https:', 'mailto:', 'tel:'];
    return protocols.includes(parsed.protocol) ? parsed.toString() : null;
  } catch {
    return null;
  }
}

function parseLinkToken(token) {
  const image = token.startsWith('![');
  const match = token.match(/^!?\[([^\]]*)\]\(([^\s)]+)(?:\s+"([^"]*)")?\)$/);
  if (!match) return null;
  return { image, label: match[1], url: match[2], title: match[3] || '' };
}

function renderInline(text, keyPrefix = 'inline') {
  const source = String(text || '');
  const output = [];
  let cursor = 0;
  let tokenIndex = 0;

  source.replace(INLINE_TOKEN, (token, offset) => {
    if (offset > cursor) output.push(source.slice(cursor, offset));
    const key = `${keyPrefix}-${tokenIndex++}`;

    if (token.startsWith('[') || token.startsWith('![')) {
      const parsed = parseLinkToken(token);
      const url = parsed && safeUrl(parsed.url, { image: parsed.image });
      if (!parsed || !url) {
        output.push(token);
      } else if (parsed.image) {
        output.push(
          <img
            key={key}
            className="knowledge-inline-image"
            src={url}
            alt={parsed.label}
            title={parsed.title || undefined}
            loading="lazy"
          />
        );
      } else {
        const external = /^https?:/i.test(url);
        output.push(
          <a
            key={key}
            href={url}
            title={parsed.title || undefined}
            target={external ? '_blank' : undefined}
            rel={external ? 'noopener noreferrer' : undefined}
          >
            {parsed.label}
          </a>
        );
      }
    } else if (token.startsWith('`')) {
      output.push(<code key={key}>{token.slice(1, -1)}</code>);
    } else if (token.startsWith('**') || token.startsWith('__')) {
      output.push(<strong key={key}>{renderInline(token.slice(2, -2), `${key}-strong`)}</strong>);
    } else if (token.startsWith('~~')) {
      output.push(<del key={key}>{renderInline(token.slice(2, -2), `${key}-del`)}</del>);
    } else {
      output.push(<em key={key}>{renderInline(token.slice(1, -1), `${key}-em`)}</em>);
    }

    cursor = offset + token.length;
    return token;
  });

  if (cursor < source.length) output.push(source.slice(cursor));
  return output;
}

function renderMultilineInline(lines, keyPrefix) {
  return lines.flatMap((line, index) => {
    const content = renderInline(line, `${keyPrefix}-${index}`);
    return index === lines.length - 1 ? content : [...content, <br key={`${keyPrefix}-br-${index}`} />];
  });
}

function splitTableRow(line) {
  let value = String(line || '').trim();
  if (value.startsWith('|')) value = value.slice(1);
  if (value.endsWith('|')) value = value.slice(0, -1);
  return value.split('|').map(cell => cell.trim());
}

function isTableSeparator(line) {
  const cells = splitTableRow(line);
  return cells.length > 0 && cells.every(cell => /^:?-{3,}:?$/.test(cell));
}

function tableAlignment(separatorCell) {
  const left = separatorCell.startsWith(':');
  const right = separatorCell.endsWith(':');
  if (left && right) return 'center';
  if (right) return 'right';
  return 'left';
}

function isBlockStart(lines, index) {
  const line = lines[index] || '';
  const next = lines[index + 1] || '';
  return (
    /^\s*```/.test(line) ||
    /^\s{0,3}#{1,6}\s+/.test(line) ||
    ATTACHMENT_BLOCK.test(line) ||
    /^\s{0,3}>\s?/.test(line) ||
    /^\s{0,3}[-+*]\s+/.test(line) ||
    /^\s{0,3}\d+[.)]\s+/.test(line) ||
    /^\s{0,3}(?:-{3,}|\*{3,}|_{3,})\s*$/.test(line) ||
    (line.includes('|') && isTableSeparator(next))
  );
}

function renderBlocks(markdown, attachments = []) {
  const lines = String(markdown || '').replace(/\r\n?/g, '\n').split('\n');
  const attachmentMap = new Map((attachments || []).map(attachment => [String(attachment.id), attachment]));
  const blocks = [];
  let index = 0;
  let blockIndex = 0;

  while (index < lines.length) {
    const line = lines[index];
    if (!line.trim()) {
      index += 1;
      continue;
    }

    const key = `knowledge-block-${blockIndex++}`;
    const attachmentBlock = line.match(ATTACHMENT_BLOCK);
    if (attachmentBlock) {
      const attachment = attachmentMap.get(attachmentBlock[1]);
      blocks.push(
        attachment
          ? <KnowledgeMedia key={key} attachment={attachment} />
          : <div key={key} className="knowledge-embedded-missing">Вложение недоступно или удалено</div>
      );
      index += 1;
      continue;
    }
    const fence = line.match(/^\s*```([^`]*)$/);
    if (fence) {
      const language = fence[1].trim();
      const code = [];
      index += 1;
      while (index < lines.length && !/^\s*```\s*$/.test(lines[index])) {
        code.push(lines[index]);
        index += 1;
      }
      if (index < lines.length) index += 1;
      blocks.push(
        <pre key={key} className="knowledge-code-block">
          <code data-language={language || undefined}>{code.join('\n')}</code>
        </pre>
      );
      continue;
    }

    const heading = line.match(/^\s{0,3}(#{1,6})\s+(.+?)\s*#*\s*$/);
    if (heading) {
      const level = Math.min(heading[1].length + 1, 6);
      blocks.push(React.createElement(`h${level}`, { key }, renderInline(heading[2], key)));
      index += 1;
      continue;
    }

    if (/^\s{0,3}(?:-{3,}|\*{3,}|_{3,})\s*$/.test(line)) {
      blocks.push(<hr key={key} />);
      index += 1;
      continue;
    }

    if (line.includes('|') && isTableSeparator(lines[index + 1] || '')) {
      const headers = splitTableRow(line);
      const separators = splitTableRow(lines[index + 1]);
      const rows = [];
      index += 2;
      while (index < lines.length && lines[index].trim() && lines[index].includes('|')) {
        rows.push(splitTableRow(lines[index]));
        index += 1;
      }
      blocks.push(
        <div key={key} className="knowledge-table-wrap">
          <table>
            <thead>
              <tr>
                {headers.map((cell, cellIndex) => (
                  <th key={`${key}-head-${cellIndex}`} style={{ textAlign: tableAlignment(separators[cellIndex] || '---') }}>
                    {renderInline(cell, `${key}-head-${cellIndex}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIndex) => (
                <tr key={`${key}-row-${rowIndex}`}>
                  {headers.map((_, cellIndex) => (
                    <td key={`${key}-cell-${rowIndex}-${cellIndex}`} style={{ textAlign: tableAlignment(separators[cellIndex] || '---') }}>
                      {renderInline(row[cellIndex] || '', `${key}-cell-${rowIndex}-${cellIndex}`)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      continue;
    }

    if (/^\s{0,3}>\s?/.test(line)) {
      const quoteLines = [];
      while (index < lines.length && /^\s{0,3}>\s?/.test(lines[index])) {
        quoteLines.push(lines[index].replace(/^\s{0,3}>\s?/, ''));
        index += 1;
      }
      blocks.push(<blockquote key={key}>{renderMultilineInline(quoteLines, key)}</blockquote>);
      continue;
    }

    const unordered = line.match(/^\s{0,3}[-+*]\s+(.+)$/);
    const ordered = line.match(/^\s{0,3}\d+[.)]\s+(.+)$/);
    if (unordered || ordered) {
      const orderedList = !!ordered;
      const listPattern = orderedList ? /^\s{0,3}\d+[.)]\s+(.+)$/ : /^\s{0,3}[-+*]\s+(.+)$/;
      const items = [];
      while (index < lines.length) {
        const item = lines[index].match(listPattern);
        if (!item) break;
        items.push(item[1]);
        index += 1;
      }
      const ListTag = orderedList ? 'ol' : 'ul';
      blocks.push(
        <ListTag key={key}>
          {items.map((item, itemIndex) => <li key={`${key}-${itemIndex}`}>{renderInline(item, `${key}-${itemIndex}`)}</li>)}
        </ListTag>
      );
      continue;
    }

    const paragraph = [line];
    index += 1;
    while (index < lines.length && lines[index].trim() && !isBlockStart(lines, index)) {
      paragraph.push(lines[index]);
      index += 1;
    }
    blocks.push(<p key={key}>{renderMultilineInline(paragraph, key)}</p>);
  }

  return blocks;
}

export default function KnowledgeContent({ content, attachments = [], emptyText = 'Содержимое статьи пока не добавлено.' }) {
  if (!String(content || '').trim()) {
    return <div className="knowledge-content-empty">{emptyText}</div>;
  }

  return <div className="knowledge-markdown">{renderBlocks(content, attachments)}</div>;
}
